import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useHistory, NavLink } from 'react-router-dom';
import AppConstants from '../../appConstants';
import logo from '../../assets/images/lovrican-africa.svg';

/**
 * Shared public header. The burger overlay is the only nav on mobile/tablet.
 *
 * `desktopNav` opts a page into the desktop header nav from Figma
 * (file YpQPMxbgd0jV8A2U0SHdKT — 81:73 main landing, 81:137 country pages),
 * which replaces the burger from 1200px up. It is opt-in because this
 * component is also used by login, register, affiliate and about, whose light
 * backgrounds would swallow the nav's white text.
 *
 * `showJoin` renders the "Join Lovrican" pill: present on the main landing
 * (81:125), absent on the country pages, which show Member Login only.
 *
 * `pinned` renders a fixed bar holding the burger and a centred logo, both on
 * one vertical centre line, which darkens once the page scrolls. It is for the
 * Be a Matchmaker flow, which the releases sheet asks to give a burger/side
 * menu (Development r6, r23) even though the Figma frames show a logo-only
 * header. The logo lives in the bar, not in each step, so it stays visible
 * while the page scrolls instead of disappearing under the bar.
 */
const HomeHeaderMobile = ({
  pageLogo,
  desktopNav = false,
  showJoin = true,
  pinned = false
}) => {
  const history = useHistory();
  const [menuToggle, setMenuToggle] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isAffiliatePage = location.pathname.split('/')[1] === 'affiliate';

  // The pages that render their own scroll-pinned header get `.headerAdd`
  // from the jQuery snippet in public/index.html, which only knows about
  // `.top-banner`, `.main_home` and `.about-top`. The pinned header
  // (Be a Matchmaker) sits in none of those, so it tracks scroll itself —
  // it is `position: fixed` and would otherwise sit unreadable on top of
  // the heading as the page scrolls under it.
  useEffect(() => {
    if (!pinned) return undefined;

    const onScroll = () => setIsScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pinned]);

  // The country pages (`/:isoCode` — two letters, e.g. /ng) are home pages too,
  // so "Home" carries the active underline there as well. Figma 81:137 shows it.
  const isHomeLinkActive = useCallback(
    (match, { pathname }) =>
      [AppConstants.routes.home].includes(pathname) ||
      /^\/[A-Za-z]{2}$/.test(pathname),
    []
  );

  const isAboutLinkActive = useCallback(
    (match, { pathname }) => [AppConstants.routes.about].includes(pathname),
    []
  );

  const isContactLinkActive = useCallback(
    (match, { pathname }) => [AppConstants.routes.contact].includes(pathname),
    []
  );

  const isFAQLinkActive = useCallback(
    (match, { pathname }) => [AppConstants.routes.faq].includes(pathname),
    []
  );

  return (
    <>
      <div
        className={`common-top-logo${desktopNav ? ' has-desktop-nav' : ''}${
          pinned ? ' pinned-header' : ''
        }${pinned && isScrolled ? ' is-scrolled' : ''}`}
      >
        <div className="burger-menu">
          {/* Was an aria-hidden <div>: the side menu is the only nav on
              mobile/tablet, and it could not be reached by keyboard or
              announced at all. */}
          <button
            type="button"
            className="burger"
            aria-label="Open menu"
            aria-expanded={menuToggle}
            onClick={() => setMenuToggle(true)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        <div
          className={`logo${pinned ? ' d-none' : ''}`}
          // onClick={() => {
          //   history.push('/');
          // }}
          // onKeyDown={(event) => event.key === 'Enter' && history.push('/')}
          role="button"
          tabIndex="0"
        >
          <img
            src={pageLogo === undefined ? logo : `/uploads/${pageLogo}`}
            className="img-fluid"
            alt="Lovrican Logo"
            onClick={() => {
              if (isAffiliatePage) {
                history.push('/affiliate');
              } else {
                history.push('/');
              }
            }}
            onKeyDown={(event) => event.key === 'Enter' && history.push('/')}
            aria-hidden="true"
          />
        </div>

        {pinned && (
          // Its own element rather than `.logo`, which style.scss positions
          // and sizes for the home banner at several breakpoints.
          <button
            type="button"
            className="pinned-logo"
            aria-label="Lovrican home"
            onClick={() => history.push(AppConstants.routes.home)}
          >
            <img src={logo} alt="Lovrican" />
          </button>
        )}

        {desktopNav && (
          <nav className="home-desktop-nav" aria-label="Main">
            <ul className="hdn-links list-unstyled">
              <li>
                <NavLink
                  to={
                    isAffiliatePage
                      ? AppConstants.routes.affiliate.home
                      : AppConstants.routes.home
                  }
                  activeClassName="active"
                  isActive={isHomeLinkActive}
                >
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={AppConstants.routes.about}
                  activeClassName="active"
                  isActive={isAboutLinkActive}
                  target="_blank"
                >
                  About us
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={AppConstants.routes.beAMatchmaker}
                  activeClassName="active"
                >
                  Be a Matchmaker
                </NavLink>
              </li>
              <li>
                <a
                  href="https://lovrican.com/blog/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Blog
                </a>
              </li>
              <li>
                <NavLink
                  to={AppConstants.routes.contact}
                  activeClassName="active"
                  isActive={isContactLinkActive}
                  target="_blank"
                >
                  Contact us
                </NavLink>
              </li>
            </ul>

            <div className="hdn-actions">
              {showJoin && (
                <button
                  type="button"
                  className="hdn-btn hdn-btn-join"
                  onClick={() => history.push(AppConstants.routes.register)}
                >
                  Join Lovrican
                </button>
              )}
              <button
                type="button"
                className="hdn-btn hdn-btn-login"
                onClick={() => history.push(AppConstants.routes.login)}
              >
                Member Login
              </button>
            </div>
          </nav>
        )}

        {menuToggle && (
          <div className="navbar-mobile outer_menu">
            <div className="menu-wrapper">
              <div className="d-block position-relative">
                <button
                  type="button"
                  className="close"
                  aria-hidden="true"
                  onClick={() => setMenuToggle(false)}
                >
                  <span aria-hidden="true">×</span>
                  <span className="sr-only">Close</span>
                </button>
              </div>

              <ul className="list-unstyled">
                <li>
                  <NavLink
                    to={isAffiliatePage ? AppConstants.routes.affiliate.home : AppConstants.routes.home}
                    activeClassName="active"
                    isActive={isHomeLinkActive}
                  >
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={AppConstants.routes.about}
                    activeClassName="active"
                    isActive={isAboutLinkActive}
                    target="_blank"
                  >
                    About Us
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={AppConstants.routes.beAMatchmaker}
                    activeClassName="active"
                  >
                    Be a Matchmaker
                  </NavLink>
                </li>
                <li>
                  <a
                    href="https://lovrican.com/blog/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <NavLink
                    to={AppConstants.routes.faq}
                    activeClassName="active"
                    isActive={isFAQLinkActive}
                    target="_blank"
                  >
                    FAQ
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={AppConstants.routes.contact}
                    activeClassName="active"
                    isActive={isContactLinkActive}
                    target="_blank"
                  >
                    Contact Us
                  </NavLink>
                </li>
              </ul>

              <div className="d-block link-btn">
                <div className='subHead'>Find your <span>ideal African soulmate</span></div>
                <span
                  className="round-button mt-0 active"
                  role="button"
                  tabIndex="0"
                  aria-hidden="true"
                  onClick={() => history.push(AppConstants.routes.register)}
                >
                  Join Lovrican
                </span>
                <span
                  className="round-button mt-0"
                  role="button"
                  tabIndex="0"
                  aria-hidden="true"
                  onClick={() => history.push(AppConstants.routes.login)}
                >
                  Login
                </span>
                {/* Affiliate section removed from the burger menu:
                    "Make money with Lovrican", "Join Affiliate", Affiliate Login. */}
              </div>

              <div className="sideMenu-bottom-blog">
                <a
                  href="https://lovrican.com/blog/"
                  target="_blank"
                  rel="noreferrer"
                >
                  BLOG
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HomeHeaderMobile;
